import { useEffect, useMemo, useState } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  EditableToken,
  RichTextContent,
  useContentToken,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, ChevronDown, Search, X } from 'lucide-react';
import { alaViewport } from './alaMotion';
import {
  NEWS_TOPICS,
  newsArticles,
  type NewsArticle,
  type NewsTopic,
} from './newsArticles';
import { plainText } from './propertyUtils';

const PAGE_SIZE = 6;

function EyebrowDot() {
  return (
    <span className="h-[9px] min-h-[9px] w-[9px] shrink-0 rounded-full bg-[rgb(var(--ala-accent))]" aria-hidden />
  );
}

function articleMatchesQuery(article: NewsArticle, query: string) {
  if (!query) return true;
  const haystack = `${article.title} ${article.excerpt} ${article.topic} ${article.dateLabel}`.toLowerCase();
  return haystack.includes(query);
}

function ArticleCard({ article, index }: { article: NewsArticle; index: number }) {
  return (
    <motion.a
      href={article.path}
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={alaViewport}
      transition={{ duration: 0.55, delay: 0.05 * (index % 3) }}
      className="group flex flex-col"
    >
      <div className="relative aspect-[16/10] w-full overflow-hidden bg-[rgb(var(--ala-dark))]">
        <VibesitesImage
          url={article.image}
          alt={article.imageAlt}
          sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"
          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
        />
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-1">
        <p className="font-heading text-xs font-semibold uppercase tracking-[0.16em] text-[rgb(var(--ala-secondary))]">
          {article.dateLabel}
        </p>
        <span className="font-heading text-xs font-medium uppercase tracking-[0.12em] text-[rgb(var(--ala-dark))]/45">
          {article.topic}
        </span>
      </div>
      <h3 className="mt-2 font-heading text-xl font-medium leading-snug tracking-[-0.02em] text-[rgb(var(--ala-dark))] transition-colors group-hover:text-[rgb(var(--ala-accent))] md:text-[22px]">
        {article.title}
      </h3>
      <p className="mt-2 line-clamp-3 font-heading text-sm leading-relaxed text-[rgb(var(--ala-dark))]/65">
        {article.excerpt}
      </p>
      <span className="mt-4 inline-flex items-center gap-2 font-heading text-sm font-semibold uppercase tracking-[0.08em] text-[rgb(var(--ala-dark))] transition-colors group-hover:text-[rgb(var(--ala-accent))]">
        Read more
        <ArrowRight className="h-3.5 w-3.5" />
      </span>
    </motion.a>
  );
}

function Pagination({
  page,
  totalPages,
  onChange,
}: {
  page: number;
  totalPages: number;
  onChange: (page: number) => void;
}) {
  if (totalPages <= 1) return null;

  const pages = Array.from({ length: totalPages }, (_, i) => i + 1);

  return (
    <nav
      className="mt-14 flex flex-col items-center gap-4 border-t border-[rgb(var(--ala-dark))]/10 pt-10 sm:flex-row sm:justify-between"
      aria-label="News pagination"
    >
      <p className="font-heading text-sm text-[rgb(var(--ala-dark))]/60">
        Page {page} of {totalPages}
      </p>
      <div className="flex flex-wrap items-center justify-center gap-2">
        <button
          type="button"
          onClick={() => onChange(page - 1)}
          disabled={page <= 1}
          className="inline-flex h-10 items-center gap-2 rounded-full border border-[rgb(var(--ala-dark))]/15 bg-[rgb(var(--ala-background))] px-4 font-heading text-sm font-medium text-[rgb(var(--ala-dark))] transition hover:border-[rgb(var(--ala-dark))] disabled:cursor-not-allowed disabled:opacity-40"
          aria-label="Previous page"
        >
          <ArrowLeft className="h-4 w-4" />
          Prev
        </button>
        {pages.map((n) => {
          const active = n === page;
          return (
            <button
              key={n}
              type="button"
              onClick={() => onChange(n)}
              aria-current={active ? 'page' : undefined}
              className={`inline-flex h-10 min-w-10 items-center justify-center rounded-full px-3 font-heading text-sm font-medium transition ${
                active
                  ? 'bg-[rgb(var(--ala-dark))] text-[rgb(var(--ala-on-primary))]'
                  : 'border border-[rgb(var(--ala-dark))]/15 bg-[rgb(var(--ala-background))] text-[rgb(var(--ala-dark))] hover:border-[rgb(var(--ala-dark))]'
              }`}
            >
              {n}
            </button>
          );
        })}
        <button
          type="button"
          onClick={() => onChange(page + 1)}
          disabled={page >= totalPages}
          className="inline-flex h-10 items-center gap-2 rounded-full border border-[rgb(var(--ala-dark))]/15 bg-[rgb(var(--ala-background))] px-4 font-heading text-sm font-medium text-[rgb(var(--ala-dark))] transition hover:border-[rgb(var(--ala-dark))] disabled:cursor-not-allowed disabled:opacity-40"
          aria-label="Next page"
        >
          Next
          <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </nav>
  );
}

export function NewsContent() {
  const [query, setQuery] = useState('');
  const [topic, setTopic] = useState<'all' | NewsTopic>('all');
  const [page, setPage] = useState(1);

  const searchPlaceholder =
    plainText(useContentToken('newscontent.search_placeholder')) || 'Search articles';

  const normalizedQuery = query.trim().toLowerCase();
  const isFiltering = Boolean(normalizedQuery) || topic !== 'all';

  const filtered = useMemo(() => {
    return newsArticles.filter((article) => {
      if (topic !== 'all' && article.topic !== topic) return false;
      return articleMatchesQuery(article, normalizedQuery);
    });
  }, [normalizedQuery, topic]);

  const featured = newsArticles[0];
  const topPosts = newsArticles.slice(1, 4);
  const archiveSource = isFiltering ? filtered : newsArticles.slice(4);

  const totalPages = Math.max(1, Math.ceil(archiveSource.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = archiveSource.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  useEffect(() => {
    setPage(1);
  }, [normalizedQuery, topic]);

  const goToPage = (next: number) => {
    setPage(Math.min(Math.max(1, next), totalPages));
  };

  return (
    <div className="w-full bg-[rgb(var(--ala-background))]">
      {/* Intro */}
      <section className="w-full overflow-clip bg-[rgb(var(--ala-background))] py-16 md:py-24">
        <div className="mx-auto max-w-[1440px] px-6 md:px-[clamp(16px,6vw,87px)]">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={alaViewport}
            transition={{ duration: 0.7 }}
            className="max-w-[720px]"
          >
            <div className="mb-4 flex items-center gap-3">
              <EyebrowDot />
              <RichTextContent
                path="newscontent.intro_eyebrow"
                label="Intro Eyebrow"
                as="span"
                className="font-heading text-base font-medium uppercase tracking-[2px] text-[rgb(var(--ala-dark))]/60"
              />
            </div>
            <RichTextContent
              path="newscontent.intro_headline"
              label="Intro Headline"
              as="h2"
              className="font-heading text-[clamp(28px,3.2vw,44px)] font-medium leading-[1.1] tracking-[-0.03em] text-[rgb(var(--ala-dark))]"
            />
            <RichTextContent
              path="newscontent.intro_body"
              label="Intro Body"
              as="div"
              className="mt-5 font-heading text-base font-normal leading-relaxed text-[rgb(var(--ala-dark))]/70 [&_p]:mb-4 [&_p:last-child]:mb-0"
            />
          </motion.div>
        </div>
      </section>

      {/* Featured / Top posts */}
      {featured ? (
        <section className="w-full overflow-clip bg-[#F7F7F7] py-16 md:py-24">
          <div className="mx-auto max-w-[1440px] px-6 md:px-[clamp(16px,6vw,87px)]">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={alaViewport}
              transition={{ duration: 0.6 }}
              className="mb-10 flex items-center gap-3 md:mb-14"
            >
              <EyebrowDot />
              <RichTextContent
                path="newscontent.featured_eyebrow"
                label="Featured Eyebrow"
                as="span"
                className="font-heading text-base font-medium uppercase tracking-[2px] text-[rgb(var(--ala-dark))]/60"
              />
            </motion.div>

            <div className="grid grid-cols-1 gap-12 lg:grid-cols-[1.35fr_1fr] lg:gap-16">
              <motion.a
                href={featured.path}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={alaViewport}
                transition={{ duration: 0.7 }}
                className="group flex flex-col"
              >
                <div className="relative aspect-[16/10] w-full overflow-hidden bg-[rgb(var(--ala-dark))]">
                  <VibesitesImage
                    url={featured.image}
                    alt={featured.imageAlt}
                    sizes="(min-width: 1024px) 55vw, 100vw"
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[rgb(var(--ala-dark))]/50 to-transparent opacity-80" />
                </div>
                <div className="mt-5 flex flex-wrap items-center gap-x-3 gap-y-1">
                  <p className="font-heading text-sm font-semibold uppercase tracking-[0.16em] text-[rgb(var(--ala-secondary))]">
                    {featured.dateLabel}
                  </p>
                  <span className="font-heading text-xs font-medium uppercase tracking-[0.12em] text-[rgb(var(--ala-dark))]/45">
                    {featured.topic}
                  </span>
                </div>
                <h3 className="mt-2 font-heading text-[clamp(24px,2.6vw,36px)] font-medium leading-[1.15] tracking-[-0.02em] text-[rgb(var(--ala-dark))] transition-colors group-hover:text-[rgb(var(--ala-accent))]">
                  {featured.title}
                </h3>
                <p className="mt-3 max-w-[52ch] font-heading text-base leading-relaxed text-[rgb(var(--ala-dark))]/70">
                  {featured.excerpt}
                </p>
                <span className="mt-5 inline-flex items-center gap-2 font-heading text-sm font-semibold uppercase tracking-[0.08em] text-[rgb(var(--ala-dark))] transition-colors group-hover:text-[rgb(var(--ala-accent))]">
                  Read article
                  <ArrowRight className="h-4 w-4" />
                </span>
              </motion.a>

              <div className="flex flex-col divide-y divide-[rgb(var(--ala-dark))]/[0.12] border-t border-[rgb(var(--ala-dark))]/[0.12] lg:border-t-0 lg:border-l lg:pl-12 lg:pt-0">
                {topPosts.map((article, index) => (
                  <motion.a
                    key={article.slug}
                    href={article.path}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={alaViewport}
                    transition={{ duration: 0.55, delay: 0.06 * index }}
                    className="group flex gap-5 py-6 first:pt-0 last:pb-0 md:gap-6"
                  >
                    <div className="relative hidden h-[88px] w-[112px] shrink-0 overflow-hidden bg-[rgb(var(--ala-dark))] sm:block">
                      <VibesitesImage
                        url={article.image}
                        alt={article.imageAlt}
                        sizes="112px"
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                        <p className="font-heading text-xs font-semibold uppercase tracking-[0.16em] text-[rgb(var(--ala-secondary))]">
                          {article.dateLabel}
                        </p>
                        <span className="font-heading text-[10px] font-medium uppercase tracking-[0.12em] text-[rgb(var(--ala-dark))]/45">
                          {article.topic}
                        </span>
                      </div>
                      <h3 className="mt-1.5 font-heading text-lg font-medium leading-snug tracking-[-0.02em] text-[rgb(var(--ala-dark))] transition-colors group-hover:text-[rgb(var(--ala-accent))] md:text-xl">
                        {article.title}
                      </h3>
                    </div>
                  </motion.a>
                ))}
              </div>
            </div>
          </div>
        </section>
      ) : null}

      {/* Latest / filtered archive */}
      <section className="w-full overflow-clip bg-[rgb(var(--ala-background))] py-16 md:py-28">
        <div className="mx-auto max-w-[1440px] px-6 md:px-[clamp(16px,6vw,87px)]">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={alaViewport}
            transition={{ duration: 0.6 }}
            className="mb-10 md:mb-14"
          >
            <div className="mb-4 flex items-center gap-3">
              <EyebrowDot />
              <RichTextContent
                path="newscontent.latest_eyebrow"
                label="Latest Eyebrow"
                as="span"
                className="font-heading text-base font-medium uppercase tracking-[2px] text-[rgb(var(--ala-dark))]/60"
              />
            </div>
            <RichTextContent
              path="newscontent.latest_headline"
              label="Latest Headline"
              as="h2"
              className="font-heading text-[clamp(28px,3.2vw,44px)] font-medium leading-[1.1] tracking-[-0.03em] text-[rgb(var(--ala-dark))]"
            />

            <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center">
              <label className="relative block min-w-0 flex-1">
                <span className="sr-only">Search news articles</span>
                <Search
                  className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[rgb(var(--ala-dark))]/40"
                  aria-hidden
                />
                <input
                  type="search"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder={searchPlaceholder}
                  className="w-full rounded-xl border border-[rgb(var(--ala-dark))]/15 bg-[rgb(var(--ala-background))] py-3.5 pl-12 pr-12 font-heading text-base text-[rgb(var(--ala-dark))] outline-none transition placeholder:text-[rgb(var(--ala-dark))]/40 focus:border-[rgb(var(--ala-secondary))] focus:ring-2 focus:ring-[rgb(var(--ala-secondary))]/20"
                />
                <span className="hidden">
                  <EditableToken
                    path="newscontent.search_placeholder"
                    type="text"
                    label="Search Placeholder"
                    as="span"
                  >
                    {searchPlaceholder}
                  </EditableToken>
                </span>
                {query ? (
                  <button
                    type="button"
                    onClick={() => setQuery('')}
                    className="absolute right-3 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full text-[rgb(var(--ala-dark))]/45 transition hover:bg-[rgb(var(--ala-dark))]/5 hover:text-[rgb(var(--ala-dark))]"
                    aria-label="Clear search"
                  >
                    <X className="h-4 w-4" />
                  </button>
                ) : null}
              </label>

              <label className="relative block w-full shrink-0 sm:w-[240px]">
                <span className="sr-only">Filter by topic</span>
                <select
                  value={topic}
                  onChange={(event) =>
                    setTopic(event.target.value === 'all' ? 'all' : (event.target.value as NewsTopic))
                  }
                  className="w-full appearance-none rounded-xl border border-[rgb(var(--ala-dark))]/15 bg-[rgb(var(--ala-background))] py-3.5 pl-4 pr-11 font-heading text-base text-[rgb(var(--ala-dark))] outline-none transition focus:border-[rgb(var(--ala-secondary))] focus:ring-2 focus:ring-[rgb(var(--ala-secondary))]/20"
                >
                  <option value="all">All topics</option>
                  {NEWS_TOPICS.map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </select>
                <ChevronDown
                  className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[rgb(var(--ala-dark))]/45"
                  aria-hidden
                />
              </label>
            </div>

            {isFiltering ? (
              <p className="mt-4 font-heading text-sm text-[rgb(var(--ala-dark))]/60">
                {filtered.length === 0
                  ? 'No articles match your search.'
                  : `${filtered.length} article${filtered.length === 1 ? '' : 's'} found`}
              </p>
            ) : null}
          </motion.div>

          {pageItems.length > 0 ? (
            <>
              <div className="grid grid-cols-1 gap-x-8 gap-y-12 sm:grid-cols-2 lg:grid-cols-3">
                {pageItems.map((article, index) => (
                  <ArticleCard key={article.slug} article={article} index={index} />
                ))}
              </div>
              <Pagination page={safePage} totalPages={totalPages} onChange={goToPage} />
            </>
          ) : isFiltering ? (
            <div className="border border-[rgb(var(--ala-dark))]/10 bg-[#F7F7F7] px-6 py-12 text-center">
              <RichTextContent
                path="newscontent.empty_message"
                label="Empty Results Message"
                as="div"
                className="font-heading text-base text-[rgb(var(--ala-dark))]/70 [&_p]:mb-0"
              />
            </div>
          ) : null}
        </div>
      </section>

      {/* Closing CTA */}
      <section className="w-full overflow-clip bg-[rgb(var(--ala-dark))] py-16 md:py-24">
        <div className="mx-auto max-w-[1440px] px-6 md:px-[clamp(16px,6vw,87px)]">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={alaViewport}
            transition={{ duration: 0.7 }}
            className="max-w-[640px]"
          >
            <div className="mb-4 flex items-center gap-3">
              <span className="h-[9px] w-[9px] rounded-full bg-[rgb(var(--ala-accent))]" aria-hidden />
              <RichTextContent
                path="newscontent.cta_eyebrow"
                label="CTA Eyebrow"
                as="span"
                className="font-heading text-base font-medium uppercase tracking-[2px] text-[rgb(var(--ala-on-primary))]/70"
              />
            </div>
            <RichTextContent
              path="newscontent.cta_headline"
              label="CTA Headline"
              as="h2"
              className="font-heading text-[clamp(28px,3.2vw,44px)] font-medium leading-[1.1] tracking-[-0.03em] text-[rgb(var(--ala-on-primary))]"
            />
            <RichTextContent
              path="newscontent.cta_body"
              label="CTA Body"
              as="div"
              className="mt-5 font-heading text-base leading-relaxed text-[rgb(var(--ala-on-primary))]/75 [&_p]:mb-0"
            />
            <a
              href="/contact-us"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-[rgb(var(--ala-accent))] px-7 py-4 font-heading text-sm font-semibold uppercase tracking-[-0.3px] text-[rgb(var(--ala-on-primary))] transition-colors hover:bg-[#B80022]"
            >
              Contact Us
              <ArrowRight className="h-4 w-4" />
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'NewsContent',
  displayName: 'News Content',
  tokens: {
    'newscontent.intro_eyebrow': {
      label: 'Intro Eyebrow',
      type: 'richtext',
      defaultValue: '<p>press room</p>',
    },
    'newscontent.intro_headline': {
      label: 'Intro Headline',
      type: 'richtext',
      defaultValue: '<p>Stories from across the American Landmark platform.</p>',
    },
    'newscontent.intro_body': {
      label: 'Intro Body',
      type: 'richtext',
      defaultValue:
        '<p>Browse our latest acquisitions, leadership updates, and community initiatives — or open any story for the full release.</p>',
    },
    'newscontent.search_placeholder': {
      label: 'Search Placeholder',
      type: 'text',
      defaultValue: 'Search articles',
    },
    'newscontent.featured_eyebrow': {
      label: 'Featured Eyebrow',
      type: 'richtext',
      defaultValue: '<p>Top posts</p>',
    },
    'newscontent.latest_eyebrow': {
      label: 'Latest Eyebrow',
      type: 'richtext',
      defaultValue: '<p>archive</p>',
    },
    'newscontent.latest_headline': {
      label: 'Latest Headline',
      type: 'richtext',
      defaultValue: '<p>Latest</p>',
    },
    'newscontent.empty_message': {
      label: 'Empty Results Message',
      type: 'richtext',
      defaultValue:
        '<p>No articles match your search or topic filter. Try a different keyword or choose All topics.</p>',
    },
    'newscontent.cta_eyebrow': {
      label: 'CTA Eyebrow',
      type: 'richtext',
      defaultValue: '<p>media inquiries</p>',
    },
    'newscontent.cta_headline': {
      label: 'CTA Headline',
      type: 'richtext',
      defaultValue: '<p>Looking for more information?</p>',
    },
    'newscontent.cta_body': {
      label: 'CTA Body',
      type: 'richtext',
      defaultValue:
        '<p>Reach out to our team for press inquiries, partnership conversations, or additional background on American Landmark.</p>',
    },
  },
};

(NewsContent as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default NewsContent;
